import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, Ruler } from "lucide-react";
import { useNavigate } from "react-router-dom";
import type { Flower } from "@/hooks/useFlowersData";

const getColorValue = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    'peach': '#FFCBA4',
    'champagne': '#F7E7CE',
    'cream': '#FFFDD0',
    'mustard': '#FFDB58'
  };
  
  return colorMap[colorName.toLowerCase()] || colorName.toLowerCase();
};

interface FlowerCardProps {
  flower: Flower;
  priority?: boolean;
}

export function FlowerCard({ flower, priority = false }: FlowerCardProps) {
  const navigate = useNavigate();

  const handleClick = () => {
    navigate(`/catalog/flower/${flower.id}`);
  };

  return (
    <Card 
      className="group cursor-pointer bg-card border-border overflow-hidden"
      onClick={handleClick}
    >
      <CardHeader className="p-0">
        <div className="aspect-square relative overflow-hidden bg-muted">
          {flower.image_url ? (
            <img
              src={flower.image_url}
              alt={`${flower.name} - ${flower.color}`}
              className="w-full h-full object-cover"
              loading={priority ? "eager" : "lazy"}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-muted-foreground">
              No image
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="p-4 bg-card">
        <div className="space-y-3">
          <div>
            <h3 className="text-lg font-display font-light text-foreground mb-2">
              {flower.name}
            </h3>
            <div className="flex items-center gap-2">
              <Badge 
                variant="secondary" 
                className="bg-andean-mist text-andean-forest border-0 capitalize flex items-center gap-1"
              >
                <div 
                  className="w-2 h-2 rounded-full border border-andean-forest/30" 
                  style={{ backgroundColor: getColorValue(flower.color) }}
                />
                {flower.color}
              </Badge>
              {flower.type && (
                <Badge variant="outline" className="text-xs">
                  {flower.type}
                </Badge>
              )}
            </div>
          </div>

          <div className="space-y-2 text-sm text-muted-foreground">
            {flower.stem_length_range && (
              <div className="flex items-center gap-2">
                <Ruler className="h-3 w-3 text-andean-sage" />
                <span className="text-xs">{flower.stem_length_range}</span>
              </div>
            )}
            {flower.duration_range && (
              <div className="flex items-center gap-2">
                <Clock className="h-3 w-3 text-andean-sage" />
                <span className="text-xs">{flower.duration_range}</span>
              </div>
            )}
          </div>

          <Button 
            variant="outline" 
            size="sm" 
            className="w-full mt-3 border-primary/20 hover:bg-primary hover:text-primary-foreground font-light"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}
          >
            View Details
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}